import React from 'react';
import { Droppable, DropResult, DragDropContext } from 'react-beautiful-dnd';
import { IOption } from 'components/Select/Select';
import {
  ReportLists,
  SavedReportsWrapper,
  SavedReportsContainer,
} from './styles';
import messages from '../Messages';
import SavedReportItem from './SavedReportItem';
import CheckPointModal from 'components/CheckPointModal';
import { cloneDeep } from 'lodash';
import { IOpenItem } from 'store/entity/IOpenItem';
import {
  IProviderSurveyReport,
  IReportView,
  ISurveyItem,
  ISurveyReportMap,
  ISurveyView,
} from 'store/survey/surveyTypes';
import { ITaskReportInfo } from '../../../store/providers/providerTypes';
import { DeleteReportButton } from '../styles';
import i18n from '../../../i18n';

interface IComponentProps {
  canUpdate: boolean;
  canUpdateCheckpoint: boolean;
  isActive: boolean;
  onChooseAReport: (reportId: number, type: string) => void;
  onCopyReport: (reportId: number) => void;
  onDeleteReport: (reportId: number) => void;
  reportItem?: IOpenItem;
  reports: ISurveyReportMap;
  reportsTags: IOption[];
  saveReport: (report: IReportView) => void;
  saveReportItem: (item: IProviderSurveyReport) => Promise<any>;
  selectedReport: number | null;
  survey?: ISurveyView;
  surveyItem?: ISurveyItem;
  oldVersion?: boolean;
  searchedReportIds?: number[];
  providerId: number;
  deleteReportsInBulk: (reportIdList: number[]) => void;
}

interface IComponentStates {
  checkPointsModal: number;
  editing: boolean;
  filteredTags: IOption[];
  deletedReportList: number[];
}

const allReports: IOption = {
  value: 'SHOW_ALL_TAGS',
  label: messages.allOfTheReports,
};

class SavedReportList extends React.PureComponent<
  IComponentProps,
  IComponentStates
> {
  state: IComponentStates = {
    editing: false,
    filteredTags: /*
      Object.keys(this.props.reports).length > 1 &&
      this.props.reportsTags.length > 1 &&
      !this.props.canUpdate
        ? []
        :*/ [allReports],
    checkPointsModal: 0,
    deletedReportList: [],
  };

  handleAddRemoveItemToDeletedList = (reportId: number) => {
    if (this.state.deletedReportList.includes(reportId)) {
      this.setState({
        deletedReportList: this.state.deletedReportList.filter(
          (item) => item !== reportId
        ),
      });
    } else {
      this.setState({
        deletedReportList: [...this.state.deletedReportList, reportId],
      });
    }
  };

  onDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }
    const oldIndex = result.source.index;
    const newIndex = result.destination ? result.destination.index : 0;

    const allReports = Object.values(this.props.reports).reduce(
      (current, report) => {
        current.push(report);
        return current!;
      },
      []
    ) as IReportView[];
    let reportItem;
    if (this.props.reportItem) {
      reportItem = cloneDeep(
        this.props.reportItem!.item.data
      ) as IProviderSurveyReport;
      reportItem.taskReportList?.forEach((taskReport: ITaskReportInfo) => {
        const clone = cloneDeep(taskReport) as unknown as IReportView;
        clone.type = 'task';
        allReports.push(clone);
      });
    }
    const sortedReports = allReports.sort((a, b) => {
      return (a.order || 0) - (b.order || 0);
    });
    const draggedReport: IReportView = sortedReports.find(
      (report: IReportView) => report.id === Number(result.draggableId)
    )!;
    let newOrder = 0;
    if (draggedReport) {
      if (newIndex === 0) {
        // dropped in first of list
        const bottomItemOrder = sortedReports[newIndex].order!;
        newOrder = bottomItemOrder - 1;
      } else if (newIndex + 1 === sortedReports.length) {
        // dropped in end of list
        const topItemOrder = sortedReports[newIndex].order!;
        newOrder = topItemOrder + 1;
      } else if (newIndex > oldIndex) {
        // pull down a list item
        const topItemOrder = sortedReports[newIndex].order!;
        const bottomItemOrder = sortedReports[newIndex + 1].order!;
        newOrder = (topItemOrder + bottomItemOrder) / 2;
      } else {
        // pull up a list item
        const topItemOrder = sortedReports[newIndex - 1].order!;
        const bottomItemOrder = sortedReports[newIndex].order!;
        newOrder = (topItemOrder + bottomItemOrder) / 2;
      }
      const newReport = cloneDeep(draggedReport);
      newReport.order = newOrder;
      if (newReport['type'] !== 'task') this.props.saveReport(newReport);
      else if (reportItem) {
        const ref = reportItem.taskReportList?.find(
          (it) => it.id === newReport.id
        );
        if (ref) {
          ref.order = newOrder;
          this.props.saveReportItem(reportItem);
        }
      }
    }
  };

  handleChangeFilteredList = (filteredTags: IOption[]) => {
    this.setState({ filteredTags });
  };

  hasTag = (reportTags: IReportView['tags']) => {
    // checks whether a report has selected tag
    return this.state.filteredTags.find((t) => reportTags.includes(t.label));
  };

  handleShowCheckPointModal = (reportId: number) => () => {
    this.setState({
      checkPointsModal: reportId,
    });
  };

  render() {
    const {
      reports,
      isActive,
      selectedReport,
      canUpdate,
      reportItem,
      survey,
      surveyItem,
    } = this.props;
    const showAllReports =
      !this.state.filteredTags.length ||
      this.state.filteredTags.find((tag) => tag === allReports);

    const filteredReports = Object.values(reports).reduce((current, report) => {
      if (
        report.type !== 'task' &&
        report.viewOnlyInMainOwner &&
        surveyItem &&
        surveyItem.upstreamItemId
      )
        return current;
      if (showAllReports || report.tags.some(this.hasTag)) {
        current.push(report);
      }
      return current;
    }, []) as Array<IReportView | ITaskReportInfo>;

    (reportItem?.item.data as IProviderSurveyReport)?.taskReportList?.forEach(
      (taskReport) => {
        const clone = cloneDeep(taskReport);
        clone.type = 'task';
        filteredReports.push(clone);
      }
    );

    return (
      <SavedReportsContainer isActive={isActive}>
        <SavedReportsWrapper>
          {!!filteredReports?.length && (
            <>
              {/* @ts-expect-error JSX error*/}
              <DragDropContext onDragEnd={this.onDragEnd}>
                <Droppable droppableId={'saved-report-list'}>
                  {(provid) => (
                    <ReportLists ref={provid.innerRef}>
                      {filteredReports
                        .sort((a, b) => {
                          return (a.order || 0) - (b.order || 0);
                        })

                        .map((report, index?: number) => {
                          return (
                            <SavedReportItem
                              canUpdate={canUpdate}
                              index={index}
                              isReportItem={!!reportItem}
                              key={report.id}
                              onChooseAReport={this.props.onChooseAReport}
                              onCopyReport={this.props.onCopyReport}
                              onDeleteReport={
                                !this.props.searchedReportIds?.includes(
                                  report.id!
                                )
                                  ? this.props.onDeleteReport
                                  : undefined
                              }
                              onOpenCheckPoints={this.handleShowCheckPointModal(
                                report.id!
                              )}
                              report={report as IReportView}
                              selectedReport={selectedReport}
                              oldVersion={this.props.oldVersion}
                              onAddRemoveReport={
                                this.handleAddRemoveItemToDeletedList
                              }
                              deleteReportList={this.state.deletedReportList}
                            />
                          );
                        })}
                      {provid.placeholder}
                    </ReportLists>
                  )}
                </Droppable>
              </DragDropContext>
            </>
          )}
          {this.state.checkPointsModal > 0 && (
            <CheckPointModal
              active={!!this.state.checkPointsModal}
              canUpdate={this.props.canUpdateCheckpoint}
              onClose={this.handleShowCheckPointModal(0)}
              report={reports[this.state.checkPointsModal]}
              survey={survey}
              surveyReport={reportItem}
              providerId={this.props.providerId}
            />
          )}
        </SavedReportsWrapper>
        {this.state.deletedReportList.length > 0 && (
          <DeleteReportButton
            onClick={() => {
              this.props.deleteReportsInBulk(this.state.deletedReportList);
              this.setState({ deletedReportList: [] });
            }}
          >
            {i18n.t('deleteReport', { ns: 'survey' })}
          </DeleteReportButton>
        )}
      </SavedReportsContainer>
    );
  }
}
export default SavedReportList;
